import { clsx } from "clsx";
import { SubventionFeedRow } from "@/features/subventions/components/feed/SubventionFeedRow";
import { SubventionEnvelope } from "@/features/subventions/components/feed/tree/SubventionEnvelope";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import {
  buildFeedTree, type FeedNode, sortNodes, unitDate,
} from "@/features/subventions/utils/buildFeedTree";
import {
  passesFilter, tierOfDate, TIER_ORDER, type TierKey,
} from "@/features/subventions/utils/subventionStatus";

type SubventionsFeedProps = {
  subsidies: EligibleSubsidy[]
  universeFilter: Set<string>
  onOpenDetail: (subsidy: EligibleSubsidy) => void
};

const TIER_PIP: Record<TierKey, string> = {
  urgent: "bg-error",
  month: "bg-warning",
  later: "bg-success",
  none: "bg-gray-400",
};

const renderNode = (node: FeedNode, onOpenDetail: (subsidy: EligibleSubsidy) => void) => (
  node.type === "env"
    ? <SubventionEnvelope key={`env-${node.id}`} node={node} depth={0} onOpenDetail={onOpenDetail} />
    : (
      <SubventionFeedRow
        key={`${node.subsidy.subsidy_perimeter_id}-${node.subsidy.subsidy_perimeter_name}`}
        subsidy={node.subsidy}
        onOpenDetail={onOpenDetail}
      />
    )
);

export const SubventionsFeed = ({ subsidies, universeFilter, onOpenDetail }: SubventionsFeedProps) => {
  const visible = subsidies.filter((subsidy) => passesFilter(subsidy, universeFilter));
  const isEmpty = visible.length === 0;

  // A single, always-mounted live region carries either the count or the empty-filter message, so
  // filtering down to zero is announced (a region that unmounts at zero would stay silent).
  const statusMessage = isEmpty
    ? "Aucune subvention ne correspond à ce filtre."
    : `${visible.length} subvention${visible.length > 1 ? "s" : ""} affichée${visible.length > 1 ? "s" : ""}`;

  // Group into feed units (standalone cards and envelopes), then bucket each unit by tier — an
  // envelope lands in the tier of its earliest descendant deadline, intermixed with cards.
  const buckets = new Map<TierKey, { label: string, items: FeedNode[] }>();
  buildFeedTree(visible).forEach((unit) => {
    const tier = tierOfDate(unitDate(unit));
    const bucket = buckets.get(tier.key) ?? { label: tier.label, items: [] };
    bucket.items.push(unit);
    buckets.set(tier.key, bucket);
  });

  // With a single tier in play (typically a legacy org with no deadlines), the lone tier heading
  // reads as noise over the whole list, so drop the headers and render one flat list instead.
  const singleTier = buckets.size <= 1;

  return (
    <div className="flex flex-col">
      <p
        role="status"
        aria-live="polite"
        className={isEmpty ? "px-1 py-2 text-sm text-gray-500 dark:text-gray-400" : "sr-only"}
      >
        {statusMessage}
      </p>
      {TIER_ORDER.map((key) => {
        const bucket = buckets.get(key);
        if (!bucket) return null;
        return (
          <div key={key}>
            {!singleTier && (
              <div className="mb-2.5 mt-5 flex items-center gap-2.5 text-xs font-bold text-gray-600 first:mt-1 dark:text-gray-300">
                <span aria-hidden="true" className={clsx("h-[7px] w-[7px] rounded-full", TIER_PIP[key])} />
                {bucket.label}
                <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              </div>
            )}
            <div className="flex flex-col gap-2.5">
              {sortNodes(bucket.items).map((unit) => renderNode(unit, onOpenDetail))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

SubventionsFeed.displayName = "SubventionsFeed";
